<script setup lang="ts">
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@blpde/nx-icons';
import { ref, watch } from 'vue';
import { NxButton, type NxButtonSize } from '../Button';
import { NxNumberInput, UINT_PATTERN_NO_ZERO } from '../Inputs';
import type { Awaitable } from '../internal';

interface NxRecordNavigatorProps {
  /** Current page, can be bound as v-model. */
  record: number;
  /** First record */
  minRecord: number;
  /** Last record */
  maxRecord: number;
  /**
   * Sizes the whole navigator so its final height matches a button/input of the
   * same size. Defaults to `'s'`.
   */
  size?: NxButtonSize;
  showMax?: boolean;
  /** Called when the `<<` button is clicked. Must return the first record or null if not available. If not given the `minRecord` will be used.  */
  onTryFirst?: (current: number) => Awaitable<number | null>;
  /** Called when the `>>` button is clicked. Must return the last record or null if not available. If not given the `maxRecord` will be used. */
  onTryLast?: (current: number) => Awaitable<number | null>;
  /** Called when the `<` button is clicked. Must return the previous record or null if not available. */
  onTryPrev: (current: number) => Awaitable<number | null>;
  /** Called when the `>` button is clicked. Must return the next record or null if not available. */
  onTryNext: (current: number) => Awaitable<number | null>;
  /**
   * Called when `Enter` is pressed or when the onChange-event is triggered.
   * Must return the user provided record if valid, null otherwise.
   */
  onTryEnter: (newValue: number, current: number) => Awaitable<number | null>;
  /** The function that gets called if any of the other functions return a number (i.e. not null) */
  onSuccessfulTry: (target: number) => void;
}

const {
  record,
  minRecord,
  maxRecord,
  size = 's',
  showMax = true,
  onTryFirst = undefined,
  onTryLast = undefined,
  onTryPrev,
  onTryNext,
  onTryEnter,
  onSuccessfulTry,
} = defineProps<NxRecordNavigatorProps>();

/** Maps the size to its font-size token; sets the pill's em base so spacing scales. */
const FONT_SIZE_TOKEN: Record<NxButtonSize, string> = {
  xs: 'var(--nxt-font-size-xs)',
  s: 'var(--nxt-font-size-sm)',
  m: 'var(--nxt-font-size-md)',
  l: 'var(--nxt-font-size-lg)',
  xl: 'var(--nxt-font-size-xl)',
};
const recordIndex = ref<number>(record);
const onSuccessfulTryWrapped = (value: number | null) => {
  if (value === null) return;
  onSuccessfulTry(value);
};

watch(
  () => record,
  (v) => {
    recordIndex.value = v;
  }
);
</script>

<template>
  <div class="wrapper" :style="`font-size: ${FONT_SIZE_TOKEN[size]}`">
    <NxButton
      :height="size"
      width="icon-only"
      variant="ghost"
      :disabled="recordIndex <= minRecord"
      class="nav-button"
      @click="
        async () => {
          if (onTryFirst) {
            onSuccessfulTryWrapped(await onTryFirst(recordIndex));
          } else {
            onSuccessfulTryWrapped(minRecord);
          }
        }
      "
    >
      <ChevronDoubleLeftIcon filled />
    </NxButton>
    <NxButton
      :height="size"
      width="icon-only"
      variant="ghost"
      :disabled="recordIndex <= minRecord"
      class="nav-button"
      @click="
        async () => {
          onSuccessfulTryWrapped(await onTryPrev(recordIndex));
        }
      "
    >
      <ChevronLeftIcon filled />
    </NxButton>
    <div class="nav-input-wrapper">
      <NxNumberInput
        v-model="recordIndex"
        :min="minRecord"
        :max="maxRecord"
        :height="size"
        class="nav-input"
        :style="`--ch-count: ${String(maxRecord).length + 1}`"
        :pattern="UINT_PATTERN_NO_ZERO"
        @change="
          async () => {
            if (Number.isNaN(recordIndex)) return;
            const value = await onTryEnter(recordIndex, record);
            if (value === null) {
              recordIndex = record;
            } else {
              onSuccessfulTryWrapped(value);
            }
          }
        "
        @keydown.enter="
          async () => {
            if (Number.isNaN(recordIndex)) return;
            const value = await onTryEnter(recordIndex, record);
            if (value === null) {
              recordIndex = record;
            } else {
              onSuccessfulTryWrapped(value);
            }
          }
        "
      ></NxNumberInput>
      <span v-if="showMax" class="nav-max">{{ `/ ${maxRecord}` }}</span>
    </div>
    <NxButton
      :height="size"
      width="icon-only"
      variant="ghost"
      :disabled="recordIndex >= maxRecord"
      class="nav-button"
      @click="
        async () => {
          onSuccessfulTryWrapped(await onTryNext(recordIndex));
        }
      "
    >
      <ChevronRightIcon filled />
    </NxButton>
    <NxButton
      :height="size"
      width="icon-only"
      variant="ghost"
      :disabled="recordIndex >= maxRecord"
      class="nav-button"
      @click="
        async () => {
          if (onTryLast) {
            onSuccessfulTryWrapped(await onTryLast(recordIndex));
          } else {
            onSuccessfulTryWrapped(maxRecord);
          }
        }
      "
    >
      <ChevronDoubleRightIcon filled />
    </NxButton>
  </div>
</template>

<style scoped>
/* The whole row is one pill: rounded surface + border. The buttons are ghost
   (transparent until hover) and the number input is stripped of its own fill,
   border and focus ring so it melts into the pill. */
.wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background-color: var(--nxt-surface-bg);
  transition:
    border-color 0.1s,
    box-shadow 0.1s;

  /* Focus affordance moves to the pill instead of the inner input. */
  &:focus-within {
    border-color: var(--nxt-brand-bg);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-subtle-bg);
  }

  & .nav-button {
    flex: 0 0 auto;
    border-radius: var(--nxt-radius-full);

    /* Icon scales with the navigator size (em is relative to the button font). */
    & svg {
      font-size: 1.25em;
    }
  }

  .nav-input-wrapper {
    /* The input melts into the pill — it owns no fill of its own. */
    --nxt-input-bg: transparent;
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--nxt-space-2xs);

    /* Inherits the pill font-size, so it tracks the chosen size. */
    & > .nav-max {
      flex: 0 0 auto;
      font-weight: var(--nxt-font-weight-medium);
      color: var(--nxt-fg-muted);
    }
  }

  /* The number field hugs its content instead of filling the row. */
  .nav-input-wrapper :deep(.input-wrapper) {
    flex: 0 0 auto;
    width: auto;
  }

  & :deep(.nav-input) {
    width: calc(var(--ch-count) * 1ch + 2 * var(--nxt-space-2xs));
    padding-inline: var(--nxt-space-2xs);
    /* No border — keeps the field exactly as tall as the icon buttons so the
       pill height stays equal to a button/input of the same size. */
    border-width: 0;
    font-weight: var(--nxt-font-weight-medium);
    text-align: center;
  }

  /* Suppress the input's own hover/focus chrome — the pill carries it now. The
     `border: 0` also stops the focus rules re-adding a 1px border (height shift). */
  .nav-input-wrapper :deep(input:hover),
  .nav-input-wrapper :deep(.input-wrapper:focus-within input),
  .nav-input-wrapper :deep(input:focus-visible) {
    border: 0 !important;
    box-shadow: none !important;
  }
}
</style>
